import React, { useState, useCallback, useMemo, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { FormControl, InputLabel, MenuItem, Select } from '@mui/material';

import AppButton from '../../../../components/designe/app-button/AppButton';
import { useStyles } from '../Sync.styles';
import theme from '../../../../styles/theme';
import {
  useGetSyncProviders,
  useLazyGetMusclesForSyncQuery,
  useSyncMuscleMutation,
} from '../../../../services/redux/api/syncApi';
import type {
  MuscleSyncItemDto,
  SyncMuscleRequestDto,
} from '../../../../services/redux/api/syncApi';
import {
  SyncMuscleListItem,
  type SyncItemStatusDot,
} from './SyncMuscleListItem';
import { SyncMuscleJsonView } from './SyncMuscleJsonView';
import { SyncMuscleForm } from './SyncMuscleForm';
import { resetExerciseConfigApiState } from '../../../../services/redux/api/exerciseConfigAPI';
import { useDispatch } from 'react-redux';

export function SyncMusclesContent() {
  const { t } = useTranslation();
  const { classes } = useStyles(theme);
  const dispatch = useDispatch();
  const { data: providers } = useGetSyncProviders();
  const [getMuscles, { isFetching }] = useLazyGetMusclesForSyncQuery();
  const [syncMuscle, { isLoading: isSaving }] = useSyncMuscleMutation();

  const [localMuscles, setLocalMuscles] = useState<MuscleSyncItemDto[]>([]);
  const [selectedItem, setSelectedItem] = useState<MuscleSyncItemDto | null>(
    null,
  );
  const [formDirty, setFormDirty] = useState(false);

  const muscleProviders = useMemo(
    () => providers?.filter((p) => p.entities.includes('muscle')) ?? [],
    [providers],
  );
  const firstProvider = muscleProviders[0]?.provider ?? '';
  const [provider, setProvider] = useState<string>(firstProvider);

  useEffect(() => {
    if (muscleProviders.length > 0) {
      setProvider((prev) => prev || muscleProviders[0].provider);
    }
  }, [muscleProviders]);

  const currentProvider = provider || firstProvider;

  const handleLoad = useCallback(() => {
    if (!currentProvider) return;
    getMuscles(currentProvider).then((result) => {
      if (result?.data) {
        setLocalMuscles(result.data);
        setSelectedItem(null);
        setFormDirty(false);
      }
    });
  }, [getMuscles, currentProvider]);

  const handleSave = useCallback(
    (body: SyncMuscleRequestDto) => {
      if (!currentProvider) return;
      syncMuscle({ provider: currentProvider, body })
        .unwrap()
        .then((result) => {
          dispatch(resetExerciseConfigApiState());
          setLocalMuscles((prev) =>
            prev.map((item) =>
              item.providerResourceId === result.providerResourceId
                ? { ...item, localResourceId: result.localResourceId }
                : item,
            ),
          );
          setSelectedItem(null);
          setFormDirty(false);
        })
        .catch(() => {});
    },
    [currentProvider, syncMuscle],
  );

  function getStatusDot(item: MuscleSyncItemDto): SyncItemStatusDot {
    const isSelected =
      selectedItem?.providerResourceId === item.providerResourceId;
    if (isSelected && formDirty) return 'unsaved';
    return item.localResourceId ? 'synced' : 'notSynced';
  }

  return (
    <div className={classes.wrapper}>
      <div className={classes.toolbar}>
        <FormControl size="small" className={classes.providerSelect}>
          <InputLabel>{t('adminDashboard.sync.provider')}</InputLabel>
          <Select
            value={currentProvider}
            label={t('adminDashboard.sync.provider')}
            onChange={(e) => setProvider(e.target.value)}>
            {muscleProviders.map((p) => (
              <MenuItem key={p.provider} value={p.provider}>
                {p.provider}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <AppButton
          onClick={handleLoad}
          loader={isFetching}
          disabled={!currentProvider}>
          {t('adminDashboard.sync.load')}
        </AppButton>
      </div>

      <div className={classes.threeColumnLayout}>
        <div className={classes.listColumn}>
          <div className={classes.listWrapper}>
            {localMuscles.length === 0 && !isFetching ? (
              <div className={classes.emptyState}>
                {t('adminDashboard.sync.emptyList')}
              </div>
            ) : (
              localMuscles.map((item) => (
                <SyncMuscleListItem
                  key={item.providerResourceId}
                  item={item}
                  selected={
                    selectedItem?.providerResourceId === item.providerResourceId
                  }
                  statusDot={getStatusDot(item)}
                  onSelect={() => setSelectedItem(item)}
                />
              ))
            )}
          </div>
        </div>
        <SyncMuscleJsonView item={selectedItem} />
        <SyncMuscleForm
          item={selectedItem}
          provider={currentProvider}
          onSave={handleSave}
          onDirtyChange={setFormDirty}
          isSaving={isSaving}
        />
      </div>
    </div>
  );
}
